class KiwibitFeatureParts extends HTMLElement {
  connectedCallback() {
    this.desktopHeroPeek = 60;
    this.desktopCardsStep = 420;
    this.desktopAlignedWidth = 1400;
    this.desktopRightBleed = 88;
    this.desktopBleed = 88;
    this.desktopSafeGutter = 64;
    this.desktopBaseWidth = this.desktopAlignedWidth + this.desktopRightBleed;
    this.desktopExpandedWidth = this.desktopAlignedWidth + this.desktopBleed;
    this.desktopTransitionMs = 460;
    this.desktopProgressTransitionMs = 320;
    this.progressEasing = 'cubic-bezier(0.16, 1, 0.3, 1)';

    this.desktopScroller = this.querySelector('[data-kiwibit-feature-desktop-scroller]');
    this.mobileScroller = this.querySelector('[data-kiwibit-feature-mobile-scroller]');
    this.desktopCards = this.desktopScroller ? Array.from(this.desktopScroller.querySelectorAll('[data-kiwibit-feature-card]')) : [];
    this.mobileCards = this.mobileScroller ? Array.from(this.mobileScroller.querySelectorAll('[data-kiwibit-feature-card]')) : [];
    this.desktopContainer = this.querySelector('.kiwibit-feature-parts__desktop');
    this.desktopBody = this.querySelector('.kiwibit-feature-parts__body');
    this.prevButton = this.querySelector('[data-kiwibit-feature-prev]');
    this.nextButton = this.querySelector('[data-kiwibit-feature-next]');
    this.desktopProgress = this.querySelector('[data-kiwibit-feature-desktop-progress]');
    this.mobileProgress = this.querySelector('[data-kiwibit-feature-mobile-progress]');

    this.desktopCardOffsets = [];
    this.mobileCardOffsets = [];
    this.desktopSnapPoints = [];
    this.mobileSnapPoints = [];
    this.desktopActiveIndex = 0;
    this.mobileActiveIndex = 0;
    this.mobileStartIndex = 0;
    this.mobileTouchStartX = 0;
    this.mobileTouchStartScrollLeft = 0;
    this.desktopOffset = 0;
    this.desktopFrame = null;
    this.mobileFrame = null;
    this.mobileSnapFrame = null;
    this.mobileSettleTimer = null;
    this.desktopSettleTimer = null;
    this.desktopAnimating = false;
    this.desktopExpandedActive = false;
    this.mobileGestureActive = false;
    this.mobileSnapping = false;

    this.onMobileScroll = this.scheduleMobileState.bind(this);
    this.onMobileTouchStart = this.handleMobileGestureStart.bind(this);
    this.onMobileTouchEnd = this.handleMobileGestureEnd.bind(this);
    this.onPrevClick = this.scrollDesktopByCard.bind(this, -1);
    this.onNextClick = this.scrollDesktopByCard.bind(this, 1);
    this.onResize = this.handleResize.bind(this);
    this.onObservedResize = this.handleResize.bind(this);

    this.resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(this.onObservedResize)
      : null;

    if (this.desktopScroller && this.resizeObserver) {
      this.resizeObserver.observe(this.desktopScroller);
    }

    if (this.mobileScroller) {
      this.mobileScroller.addEventListener('scroll', this.onMobileScroll, { passive: true });
      this.mobileScroller.addEventListener('touchstart', this.onMobileTouchStart, { passive: true });
      this.mobileScroller.addEventListener('touchend', this.onMobileTouchEnd, { passive: true });
      this.mobileScroller.addEventListener('touchcancel', this.onMobileTouchEnd, { passive: true });
      if (this.resizeObserver) this.resizeObserver.observe(this.mobileScroller);
    }

    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onPrevClick);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onNextClick);
    }

    window.addEventListener('resize', this.onResize);
    this.handleResize();
  }

  disconnectedCallback() {
    if (this.desktopFrame) cancelAnimationFrame(this.desktopFrame);
    if (this.mobileFrame) cancelAnimationFrame(this.mobileFrame);
    if (this.mobileSnapFrame) cancelAnimationFrame(this.mobileSnapFrame);
    if (this.mobileSettleTimer) clearTimeout(this.mobileSettleTimer);
    if (this.desktopSettleTimer) clearTimeout(this.desktopSettleTimer);

    if (this.mobileScroller) {
      this.mobileScroller.removeEventListener('scroll', this.onMobileScroll);
      this.mobileScroller.removeEventListener('touchstart', this.onMobileTouchStart);
      this.mobileScroller.removeEventListener('touchend', this.onMobileTouchEnd);
      this.mobileScroller.removeEventListener('touchcancel', this.onMobileTouchEnd);
    }

    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.onPrevClick);
    }

    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.onNextClick);
    }

    window.removeEventListener('resize', this.onResize);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  handleResize() {
    this.measureCards();

    if (this.desktopActiveIndex >= this.desktopSnapPoints.length) {
      this.desktopActiveIndex = Math.max(this.desktopSnapPoints.length - 1, 0);
    }

    if (this.mobileActiveIndex >= this.mobileSnapPoints.length) {
      this.mobileActiveIndex = Math.max(this.mobileSnapPoints.length - 1, 0);
    }

    this.desktopExpandedActive = this.desktopActiveIndex > 0;
    this.applyDesktopOffset(this.desktopActiveIndex, false);
    this.applyDesktopState(this.desktopActiveIndex);
    this.snapMobileToIndex(this.mobileActiveIndex, false);
    this.scheduleMobileState();
  }

  measureCards() {
    this.desktopCardOffsets = this.desktopCards.map((card) => card.offsetLeft);
    this.mobileCardOffsets = this.mobileCards.map((card) => this.getOffsetWithinScroller(card, this.mobileScroller));
    this.desktopSnapPoints = this.buildDesktopSnapPoints();
    this.mobileSnapPoints = this.buildSnapPoints(this.mobileScroller, this.mobileCardOffsets);
  }

  getOffsetWithinScroller(card, scroller) {
    if (!card || !scroller) return 0;

    const cardRect = card.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();

    return Math.max(0, cardRect.left - scrollerRect.left + scroller.scrollLeft);
  }

  buildDesktopSnapPoints() {
    if (!this.desktopCards.length) return [0];

    const trackWidth = this.getDesktopTrackWidth();
    const maxOffset = Math.max(trackWidth - this.desktopExpandedWidth, 0);
    const snapPoints = [0];

    const pushUniquePoint = (value) => {
      const lastPoint = snapPoints[snapPoints.length - 1];
      if (Math.abs(lastPoint - value) > 1) {
        snapPoints.push(value);
      }
    };

    this.desktopCards.forEach((card, index) => {
      if (index === 0) return;
      const targetOffset = Math.max(
        0,
        Math.min(card.offsetLeft - this.desktopSafeGutter, maxOffset)
      );
      pushUniquePoint(targetOffset);
    });

    if (maxOffset > 0) {
      pushUniquePoint(maxOffset);
    }

    return snapPoints;
  }

  buildSnapPoints(scroller, cardOffsets) {
    if (!scroller || !cardOffsets.length) return [0];

    const maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
    const snapPoints = [];

    cardOffsets.forEach((offsetLeft) => {
      const clampedOffset = Math.min(offsetLeft, maxScroll);
      const lastPoint = snapPoints[snapPoints.length - 1];

      if (typeof lastPoint !== 'number' || Math.abs(lastPoint - clampedOffset) > 1) {
        snapPoints.push(clampedOffset);
      }
    });

    if (!snapPoints.length || snapPoints[0] > 1) {
      snapPoints.unshift(0);
    }

    if (maxScroll > 0) {
      const lastPoint = snapPoints[snapPoints.length - 1];
      if (Math.abs(lastPoint - maxScroll) > 1) {
        snapPoints.push(maxScroll);
      }
    }

    return snapPoints;
  }

  getNearestMobileSnapIndex(scrollLeft = this.mobileScroller?.scrollLeft || 0) {
    if (!this.mobileSnapPoints.length) return 0;

    return this.mobileSnapPoints.reduce((nearestIndex, snapPoint, index) => {
      const nearestDistance = Math.abs(this.mobileSnapPoints[nearestIndex] - scrollLeft);
      const distance = Math.abs(snapPoint - scrollLeft);
      return distance < nearestDistance ? index : nearestIndex;
    }, 0);
  }

  clampMobileIndex(index) {
    return Math.max(0, Math.min(this.mobileSnapPoints.length - 1, index));
  }

  handleMobileGestureStart(event) {
    if (!this.mobileScroller || !this.mobileSnapPoints.length) return;

    if (this.mobileSettleTimer) clearTimeout(this.mobileSettleTimer);
    if (this.mobileSnapFrame) cancelAnimationFrame(this.mobileSnapFrame);

    this.mobileGestureActive = true;
    this.mobileSnapping = false;
    this.mobileSnapFrame = null;
    this.mobileStartIndex = this.getNearestMobileSnapIndex();
    this.mobileActiveIndex = this.mobileStartIndex;
    this.mobileTouchStartScrollLeft = this.mobileScroller.scrollLeft;
    this.mobileTouchStartX = event.touches?.[0]?.clientX || 0;
  }

  handleMobileGestureEnd(event) {
    if (!this.mobileScroller || !this.mobileSnapPoints.length) return;

    const endX = event.changedTouches?.[0]?.clientX || this.mobileTouchStartX;
    const dragDelta = this.mobileTouchStartX - endX;
    const scrollDelta = this.mobileScroller.scrollLeft - this.mobileTouchStartScrollLeft;
    const movement = Math.abs(dragDelta) > 16 ? dragDelta : scrollDelta;
    const threshold = Math.min(48, this.mobileScroller.clientWidth * 0.12);
    let targetIndex = this.mobileStartIndex;

    if (Math.abs(movement) >= threshold) {
      targetIndex = this.mobileStartIndex + (movement > 0 ? 1 : -1);
    } else {
      targetIndex = null;
    }

    this.mobileGestureActive = false;
    this.scheduleMobileSnap(targetIndex);
  }

  scheduleMobileSnap(index) {
    if (!this.mobileScroller || !this.mobileSnapPoints.length) return;

    if (this.mobileSettleTimer) clearTimeout(this.mobileSettleTimer);
    if (this.mobileSnapFrame) cancelAnimationFrame(this.mobileSnapFrame);

    const requestedIndex = typeof index === 'number'
      ? this.clampMobileIndex(index)
      : null;
    const startedAt = performance.now();
    let previousLeft = this.mobileScroller.scrollLeft;
    let stableFrames = 0;

    const settle = (timestamp) => {
      const currentLeft = this.mobileScroller.scrollLeft;

      stableFrames = Math.abs(currentLeft - previousLeft) < 0.5
        ? stableFrames + 1
        : 0;
      previousLeft = currentLeft;

      if (stableFrames >= 2 || timestamp - startedAt > 420) {
        this.mobileSnapFrame = null;
        const targetIndex = requestedIndex ?? this.getNearestMobileSnapIndex();
        const targetLeft = this.mobileSnapPoints[targetIndex] || 0;

        if (Math.abs(this.mobileScroller.scrollLeft - targetLeft) > 1) {
          this.snapMobileToIndex(targetIndex, true);
          return;
        }

        this.finishMobileSnap(targetIndex);
        return;
      }

      this.mobileSnapFrame = requestAnimationFrame(settle);
    };

    this.mobileSnapFrame = requestAnimationFrame(settle);
  }

  snapMobileToIndex(index, smooth = true) {
    if (!this.mobileScroller || !this.mobileSnapPoints.length) return;

    const targetIndex = this.clampMobileIndex(index);
    const targetLeft = this.mobileSnapPoints[targetIndex] || 0;

    this.mobileActiveIndex = targetIndex;
    this.mobileSnapping = true;
    if (this.mobileSnapFrame) cancelAnimationFrame(this.mobileSnapFrame);
    if (this.mobileSettleTimer) clearTimeout(this.mobileSettleTimer);

    if (!smooth || Math.abs(this.mobileScroller.scrollLeft - targetLeft) < 1) {
      this.mobileScroller.scrollLeft = targetLeft;
      this.finishMobileSnap(targetIndex);
      return;
    }

    this.mobileScroller.scrollTo({
      left: targetLeft,
      behavior: 'smooth'
    });

    this.mobileSettleTimer = setTimeout(() => {
      this.mobileScroller.scrollLeft = targetLeft;
      this.finishMobileSnap(targetIndex);
    }, 420);
  }

  finishMobileSnap(index) {
    this.mobileActiveIndex = this.clampMobileIndex(index);
    this.mobileSnapping = false;
    this.mobileSnapFrame = null;
    if (this.mobileSettleTimer) clearTimeout(this.mobileSettleTimer);
    this.mobileSettleTimer = null;
    this.updateMobileState();
    requestAnimationFrame(() => this.updateMobileState());
  }

  scrollDesktopByCard(direction) {
    if (!this.desktopScroller || !this.desktopSnapPoints.length || this.desktopAnimating) return;

    this.resetPageInlineScroll();

    const nextIndex = Math.max(0, Math.min(this.desktopSnapPoints.length - 1, this.desktopActiveIndex + direction));
    if (nextIndex === this.desktopActiveIndex) return;

    this.desktopAnimating = true;
    this.desktopExpandedActive = nextIndex > 0 || this.desktopActiveIndex > 0;
    this.desktopActiveIndex = nextIndex;

    this.applyDesktopOffset(nextIndex, true);
    this.applyDesktopState(nextIndex);
    this.resetPageInlineScroll();

    if (this.desktopSettleTimer) clearTimeout(this.desktopSettleTimer);
    this.desktopSettleTimer = setTimeout(() => {
      this.desktopAnimating = false;
      this.desktopExpandedActive = this.desktopActiveIndex > 0;
      this.applyDesktopOffset(this.desktopActiveIndex, false);
      this.applyDesktopState(this.desktopActiveIndex);
      this.resetPageInlineScroll();
    }, this.desktopTransitionMs);
  }

  resetPageInlineScroll() {
    const scrollingElement = document.scrollingElement || document.documentElement;

    if (scrollingElement) {
      scrollingElement.scrollLeft = 0;
    }

    if (document.body) {
      document.body.scrollLeft = 0;
    }

    if (window.scrollX) {
      window.scrollTo({ left: 0, top: window.scrollY, behavior: 'auto' });
    }
  }

  applyDesktopOffset(index, animate = true) {
    if (!this.desktopScroller) return;

    const nextOffset = this.desktopSnapPoints[index] || 0;
    const renderOffset = index === 0 && this.desktopExpandedActive
      ? nextOffset - this.desktopSafeGutter
      : nextOffset;
    this.desktopOffset = nextOffset;
    this.desktopScroller.style.transition = animate
      ? `transform ${this.desktopTransitionMs}ms cubic-bezier(0.22, 1, 0.36, 1)`
      : 'none';
    this.desktopScroller.style.transform = `translate3d(${-renderOffset}px, 0, 0)`;

    if (this.desktopProgress) {
      this.desktopProgress.style.transition = animate
        ? `transform ${this.desktopProgressTransitionMs}ms ${this.progressEasing}, width ${this.desktopProgressTransitionMs}ms ${this.progressEasing}`
        : 'none';
      this.updateProgress(this.desktopScroller, this.desktopProgress);
    }
  }

  getDesktopTrackWidth() {
    if (!this.desktopCards.length) return 0;

    const lastCard = this.desktopCards[this.desktopCards.length - 1];
    return lastCard.offsetLeft + lastCard.offsetWidth;
  }

  getDesktopViewportWidth(expanded = false) {
    return expanded ? this.desktopExpandedWidth : this.desktopBaseWidth;
  }

  scheduleDesktopState() {
    if (this.desktopFrame) return;

    this.desktopFrame = requestAnimationFrame(() => {
      this.desktopFrame = null;
      this.applyDesktopState(this.desktopActiveIndex);
    });
  }

  scheduleMobileState() {
    if (this.mobileFrame) return;

    this.mobileFrame = requestAnimationFrame(() => {
      this.mobileFrame = null;
      this.updateMobileState();
    });
  }

  updateProgress(scroller, progressElement) {
    if (!scroller || !progressElement) return;

    const isDesktop = scroller === this.desktopScroller;
    const contentWidth = isDesktop ? this.getDesktopTrackWidth() : scroller.scrollWidth;
    const viewportWidth = isDesktop
      ? this.getDesktopViewportWidth(this.desktopExpandedActive || this.desktopActiveIndex > 0)
      : scroller.clientWidth;
    const maxScroll = Math.max(contentWidth - viewportWidth, 0);
    const trackWidth = progressElement.parentElement?.clientWidth || viewportWidth || 0;

    if (maxScroll <= 1 || contentWidth <= 0 || trackWidth <= 0) {
      progressElement.style.width = '100%';
      progressElement.style.transform = 'translate3d(0, 0, 0)';
      return;
    }

    const visibleRatio = Math.min(viewportWidth / contentWidth, 1);
    const thumbWidth = Math.max(trackWidth * visibleRatio, 12);
    const travel = Math.max(trackWidth - thumbWidth, 0);
    const scrollValue = isDesktop ? this.desktopOffset : scroller.scrollLeft;
    const progress = Math.min(Math.max(scrollValue / maxScroll, 0), 1);
    const translate = Math.round(travel * progress * 100) / 100;

    progressElement.style.width = `${thumbWidth}px`;
    progressElement.style.transform = `translate3d(${translate}px, 0, 0)`;
  }

  updateDesktopEdgeCards(index) {
    if (!this.desktopCards.length) return;

    const viewportLeft = this.desktopSnapPoints[index] || 0;
    const viewportWidth = this.getDesktopViewportWidth(this.desktopExpandedActive || index > 0);
    const viewportRight = viewportLeft + viewportWidth;

    this.desktopCards.forEach((card) => {
      card.classList.remove('is-edge-partial-left', 'is-edge-partial-right');

      const cardLeft = card.offsetLeft;
      const cardRight = cardLeft + card.offsetWidth;

      if (cardLeft < viewportLeft && cardRight > viewportLeft) {
        card.classList.add('is-edge-partial-left');
      }

      if (cardLeft < viewportRight && cardRight > viewportRight) {
        card.classList.add('is-edge-partial-right');
      }
    });
  }

  updateMobileEdgeCards() {
    if (!this.mobileScroller || !this.mobileCards.length) return;

    const scrollerRect = this.mobileScroller.getBoundingClientRect();
    const edgeThreshold = 2;

    this.mobileCards.forEach((card) => {
      card.classList.remove('is-edge-partial-left', 'is-edge-partial-right');

      const cardRect = card.getBoundingClientRect();
      const isVisible = cardRect.right > scrollerRect.left + edgeThreshold && cardRect.left < scrollerRect.right - edgeThreshold;
      const isLeftPartial = isVisible && cardRect.left < scrollerRect.left - edgeThreshold;
      const isRightPartial = isVisible && cardRect.right > scrollerRect.right + edgeThreshold;

      if (isLeftPartial) {
        card.classList.add('is-edge-partial-left');
      }

      if (isRightPartial) {
        card.classList.add('is-edge-partial-right');
      }
    });
  }

  updateDesktopState() {
    this.applyDesktopState(this.desktopActiveIndex);
  }

  applyDesktopState(index) {
    const atStart = index <= 0;
    const atEnd = index >= this.desktopSnapPoints.length - 1;
    const useExpandedLayout = this.desktopExpandedActive || index > 0;

    if (this.prevButton) {
      this.prevButton.disabled = this.desktopAnimating || (atStart && !useExpandedLayout);
      this.prevButton.classList.toggle('is-hidden', atStart && !useExpandedLayout);
    }

    if (this.nextButton) {
      this.nextButton.disabled = this.desktopAnimating || atEnd;
      this.nextButton.classList.toggle('is-hidden', atEnd);
    }

    if (this.desktopContainer) {
      this.desktopContainer.classList.toggle('is-cards-state', useExpandedLayout);
      this.desktopContainer.classList.toggle('has-left-fade', !atStart && useExpandedLayout);
      this.desktopContainer.classList.toggle('has-right-fade', !atEnd);
    }

    if (this.desktopBody) {
      this.desktopBody.classList.toggle('is-cards-state', useExpandedLayout);
    }

    this.updateDesktopEdgeCards(index);
    this.updateProgress(this.desktopScroller, this.desktopProgress);
  }

  updateMobileState() {
    if (!this.mobileGestureActive && !this.mobileSnapping) {
      this.mobileActiveIndex = this.getNearestMobileSnapIndex();
    }

    this.updateMobileEdgeCards();
    this.updateProgress(this.mobileScroller, this.mobileProgress);
  }
}

customElements.define('kiwibit-feature-parts', KiwibitFeatureParts);
